import { useState } from "react";

export interface BrandLogoState {
  /**
   * Render the logotype. When false, the surface draws the brand's name in
   * type instead — every surface owns what that looks like in its own
   * context, but not the decision of when to do it.
   */
  hasLogo: boolean;
  /** Wire to the `<img>`'s `onError`. */
  onLogoError: () => void;
}

/**
 * Whether a brand has a logotype a surface can actually draw.
 *
 * There are two ways a brand ends up with no usable logotype, and they are the
 * same situation once the page is rendering: the dataset has no asset at all,
 * or it has one whose URL doesn't resolve — a dead CDN link, a brand that
 * moved their file. Both have to end in the brand's name set in plain type,
 * because the alternative is a broken-image glyph standing in for the brand's
 * identity. Answering both from one hook is what keeps them from drifting into
 * two different fallbacks as surfaces get added.
 *
 * Failure is remembered per URL rather than as a flag, so a surface that
 * switches brands re-attempts the new logo instead of inheriting the previous
 * brand's failure.
 */
export const useBrandLogo = (logoSrc: string | undefined): BrandLogoState => {
  const [failedSrc, setFailedSrc] = useState<string | undefined>(undefined);

  return {
    hasLogo: logoSrc !== undefined && logoSrc !== "" && failedSrc !== logoSrc,
    onLogoError: () => setFailedSrc(logoSrc),
  };
};
